import {Helmet} from '@dr.pogodin/react-helmet';
import {openDeleteDialog, showNotification} from 'platform/components';
import {Box} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {isArray, isString} from 'ramda-adjunct';

import {
  useActivateCheckoutProfilesMutation,
  useDeactivateCheckoutProfilesMutation,
  useDeleteCheckoutProfileMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {
  catchUnhandledDataGridActions,
  DmsDataGrid,
  formatDocumentTitle,
  logError,
  refreshDmsDatagrid,
} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

import {CHECKOUT_DETAIL_ACTION_KEYS} from '../../constants/CHECKOUT_DETAIL_ACTION_KEYS';
import {CHECKOUT_GRID_ACTION_KEYS} from '../../constants/CHECKOUT_GRID_ACTION_KEYS';
import {useCheckoutRuleActions} from '../../hooks/useCheckoutRuleActions';
import {handleCheckoutApiError} from '../../utils/handleCheckoutApiError';

type CheckoutGridCode = 'checkout-profile' | 'checkout-rule';

const GRID_CONFIG: Record<
  CheckoutGridCode,
  {titleKey: string; descriptionKey: string; createActionKey: string}
> = {
  'checkout-profile': {
    titleKey: 'page.settings.labels.profiles',
    descriptionKey: 'entity.checkout.labels.profilesDescription',
    createActionKey: 'entity.checkout.actions.createNewCheckoutProfile',
  },
  'checkout-rule': {
    titleKey: 'entity.automationRule.list.title',
    descriptionKey: 'entity.checkout.labels.rulesDescription',
    createActionKey: 'entity.checkout.actions.createNewCheckoutRule',
  },
};

interface CheckoutSettingsOverviewProps {
  gridCode: CheckoutGridCode;
}

export function CheckoutSettingsOverview(props: CheckoutSettingsOverviewProps) {
  const navigate = useNavigate();
  const ruleActions = useCheckoutRuleActions();
  const [activateCheckoutProfiles] = useActivateCheckoutProfilesMutation();
  const [deactivateCheckoutProfiles] = useDeactivateCheckoutProfilesMutation();
  const [deleteCheckoutProfile] = useDeleteCheckoutProfileMutation();
  const config = GRID_CONFIG[props.gridCode];
  const title = i18n.t(config.titleKey);
  const isProfileGrid = props.gridCode === 'checkout-profile';

  const openProfileDetail = (actionKey: string, rowId: string, sourceSearchParams: string) => {
    const path = composePath(settingsRoutes.checkoutProfileDetail, {
      params: {id: rowId},
      isSearchParamsPreserved: false,
      queryParams: sourceSearchParams,
    });

    if (actionKey === 'detail_newTab') {
      window.open(path);
    } else {
      navigate(path);
    }
  };

  const profileActionCallback: ActionCallback = async ({
    actionKey,
    rowId,
    sourceSearchParams,
    refreshData,
    deselectAll,
  }) => {
    if (CHECKOUT_DETAIL_ACTION_KEYS.includes(actionKey)) {
      if (!isString(rowId)) {
        return logError(`Bulk invocation of single-row action ${actionKey} is not implemented`);
      }
      return openProfileDetail(actionKey, rowId, sourceSearchParams);
    }

    const profileIds = isArray(rowId) ? rowId : [rowId];
    // Rule grids show the profile name and its active state, so they refresh with the profiles.
    const handleSuccess = (message: string) => {
      showNotification.success(message);
      refreshData();
      deselectAll();
      refreshDmsDatagrid('checkout-rule');
      refreshDmsDatagrid('checkout-profile-rule');
    };

    await match({actionKey, rowId})
      .with({actionKey: CHECKOUT_GRID_ACTION_KEYS.setAsActive}, () =>
        activateCheckoutProfiles({checkoutProfileIds: profileIds})
          .unwrap()
          .then(() => handleSuccess(i18n.t('general.notifications.successfullyUpdated')))
          .catch(handleCheckoutApiError)
      )
      .with({actionKey: CHECKOUT_GRID_ACTION_KEYS.setAsInactive}, () =>
        deactivateCheckoutProfiles({checkoutProfileIds: profileIds})
          .unwrap()
          .then(() => handleSuccess(i18n.t('general.notifications.successfullyUpdated')))
          .catch(handleCheckoutApiError)
      )
      .with({actionKey: CHECKOUT_GRID_ACTION_KEYS.delete, rowId: Pattern.string}, (params) =>
        openDeleteDialog({
          onConfirm: () =>
            deleteCheckoutProfile({checkoutProfileId: params.rowId})
              .unwrap()
              .then(() => handleSuccess(i18n.t('general.notifications.successfullyDeleted')))
              .catch(handleCheckoutApiError),
        })
      )
      .with({actionKey: CHECKOUT_GRID_ACTION_KEYS.delete}, () =>
        logError(`Bulk invocation of single-row action ${actionKey} is not implemented`)
      )
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <>
      <Helmet title={formatDocumentTitle(title, i18n.t('page.settings.labels.title'))} />
      <SettingsTemplate
        header={{
          title,
          actions: [
            {
              type: 'button',
              title: i18n.t(config.createActionKey),
              onClick: isProfileGrid
                ? () => navigate(settingsRoutes.checkoutProfileCreate)
                : ruleActions.onCreate,
            },
          ],
        }}
        description={i18n.t(config.descriptionKey)}
        data-testid={`settings-${props.gridCode}`}
      >
        <Box height="100%" minHeight={100}>
          <DmsDataGrid
            key={props.gridCode}
            gridCode={props.gridCode}
            predefinedColumns={isProfileGrid ? undefined : ruleActions.predefinedColumns}
            actionCallback={isProfileGrid ? profileActionCallback : ruleActions.actionCallback}
            data-testid={`settings-${props.gridCode}-datagrid`}
          />
        </Box>
      </SettingsTemplate>
    </>
  );
}
